import EventDispatcher from '../event/event-dispatcher';
import Action from './action';
import GeneralUtility from '../utility/general-utility';

/**
 * Represents a plugin.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default abstract class Plugin {
  /**
   * Event for checking if the plugin is ready.
   *
   * @type {EventDispatcher<void>}
   * @private
   */
  private _readyEvent: EventDispatcher<void> = new EventDispatcher<void>();

  /**
   * Event for checking if an action is ready.
   *
   * @type {EventDispatcher<Action>}
   * @private
   */
  private _actionReadyEvent: EventDispatcher<Action> = new EventDispatcher<Action>();

  /**
   * Main containers of the plugin. Each container is a separate plugin variant.
   *
   * @type {NodeListOf<HTMLElement>}
   */
  private _containers: NodeListOf<HTMLElement>;

  /**
   * Plugin actions.
   *
   * @type {object}
   * @private
   */
  private _actions: { [key: string]: Action[] } = {};

  /**
   * Plugin name.
   *
   * @type {string}
   * @private
   */
  private readonly _name: string;


  /**
   * Constructor.
   *
   * @param {NodeListOf<HTMLElement>} containers Main containers of the plugin; each container is a separate plugin variant
   */
  protected constructor(containers: NodeListOf<HTMLElement>) {
    this._containers = containers;

    if (this._containers.length > 0) {
      // Add page UID so that plugin settings are different for plugins on different pages
      this._name = this._containers[0].dataset.plugin + '-' + GeneralUtility.pageUid;
    }
  }


  /**
   * Gets the event for checking if the plugin is ready.
   *
   * @returns {EventDispatcher<void>}
   */
  get readyEvent(): EventDispatcher<void> {
    return this._readyEvent;
  }


  /**
   * Gets the event for checking if an action is ready.
   *
   * @returns {EventDispatcher<Action>}
   */
  get actionReadyEvent(): EventDispatcher<Action> {
    return this._actionReadyEvent;
  }


  /**
   * Gets the main containers of the plugin.
   *
   * @returns {NodeListOf<HTMLElement>}
   */
  get containers(): NodeListOf<HTMLElement> {
    return this._containers;
  }


  /**
   * Sets the main containers of the plugin.
   *
   * @param {NodeListOf<HTMLElement>} containers New containers
   */
  set containers(containers: NodeListOf<HTMLElement>) {
    this._containers = containers;
  }


  /**
   * Gets all plugin actions.
   *
   * @returns {{ [key: string]: Action[] }}
   */
  get actions(): { [key: string]: Action[] } {
    return this._actions;
  }


  /**
   * Sets all plugin actions.
   *
   * @param { [key: string]: Action[] } actions New actions
   * @returns {void}
   */
  set actions(actions: { [key: string]: Action[] }) {
    this._actions = actions;
  }


  /**
   * Adds a plugin action.
   *
   * @param {Action} action Action to add
   * @returns {void}
   */
  addAction(action: Action) {
    const name = action.name;

    if (this._actions[name] === undefined) {
      this._actions[name] = [];
    }

    this._actions[name].push(action);
  }


  /**
   * Waits for each action to be ready to then return it in the given handler.
   *
   * @param {string} name Name of the action
   * @param {function} handler Returns the actions each time when they are ready
   * @returns {void}
   */
  getActions(name: string, handler: (actions: Action) => any) {
    if (this._actions.hasOwnProperty(name) && this._actions[name].length > 0) {
      for (const action of this._actions[name]) {
        handler(action);
      }
    }

    this.actionReadyEvent.register(registeredAction => {
      if (registeredAction.name === name) {
        handler(registeredAction);
      }
    });
  }


  /**
   * Waits for the first action to be ready to then return it in the given handler.
   *
   * @param {string} name Name of the action to get
   * @param {function} handler Returns the action when ready
   * @returns {void}
   */
  getAction(name: string, handler: (action: Action) => any) {
    if (this._actions.hasOwnProperty(name) && this._actions[name].length > 0) {
      handler(this._actions[name][0]);
    }

    this.actionReadyEvent.register(registeredAction => {
      if (registeredAction.name === name) {
        handler(registeredAction);
      }
    });
  }


  /**
   * Gets the plugin name.
   *
   * @returns {string}
   */
  get name(): string {
    return this._name;
  }
}
